import { cx } from '../ui/cx.ts';

/**
 * Compact SVG trend line — Dispatch Deck chart quartet (epic #897).
 *
 * Dependency-free: geometry is computed inline and every color comes
 * from a Deck utility class (`stroke-*` / `fill-*` generated by
 * `tailwind.config.ts`) — never hex, never a chart library.
 *
 * Accessibility: the svg is exposed as ONE image (`role="img"` with a
 * mandatory `ariaLabel`, mirrored into `<title>`); the geometry inside
 * is decoration. Dots carry per-point `<title>` tooltips from `labels`
 * for pointer users. A chart must never be the sole carrier of a
 * number — consumers surface the key values as text alongside
 * (DESIGN.md §9).
 *
 * Point-math edges: an empty series draws the baseline grid only; a
 * single point renders one centered dot (no polyline); an all-zero
 * series draws a flat line on the baseline (the max is floored at 1 so
 * there is no division by zero).
 */

const VIEW_WIDTH = 640;
const PADDING_X = 12;
const PADDING_Y = 10;

interface Props {
  readonly points: readonly number[];
  /** Per-point tooltip label; falls back to the numeric value. */
  readonly labels?: readonly string[];
  readonly strokeClassName?: string;
  readonly dotClassName?: string;
  /** Rendered pixel height (doubles as the viewBox height). */
  readonly height?: number;
  readonly dots?: boolean;
  readonly ariaLabel: string;
  readonly testId?: string;
}

export function Line({
  points,
  labels,
  strokeClassName = 'stroke-signal',
  dotClassName = 'fill-signal',
  height = 96,
  dots = true,
  ariaLabel,
  testId,
}: Props): JSX.Element {
  const max = Math.max(1, ...points);
  const drawableWidth = VIEW_WIDTH - PADDING_X * 2;
  const drawableHeight = height - PADDING_Y * 2;
  const coordinates = points.map((value, index) => {
    const x =
      points.length <= 1
        ? VIEW_WIDTH / 2
        : PADDING_X + (index / (points.length - 1)) * drawableWidth;
    const y = PADDING_Y + drawableHeight - (Math.max(0, value) / max) * drawableHeight;
    return { x, y };
  });

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
      preserveAspectRatio="none"
      style={{ height }}
      className="block w-full"
      data-testid={testId}
    >
      <title>{ariaLabel}</title>
      {[0, 1, 2].map((line) => {
        const y = PADDING_Y + (line / 2) * drawableHeight;
        return (
          <line
            key={line}
            x1={PADDING_X}
            x2={VIEW_WIDTH - PADDING_X}
            y1={y}
            y2={y}
            className="stroke-line-soft"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
      {coordinates.length > 1 ? (
        <polyline
          points={coordinates.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')}
          className={cx('fill-none', strokeClassName)}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      {dots
        ? coordinates.map((c, index) => (
            <circle
              key={index}
              cx={c.x.toFixed(1)}
              cy={c.y.toFixed(1)}
              r={3}
              className={dotClassName}
            >
              <title>{labels?.[index] ?? String(points[index] ?? 0)}</title>
            </circle>
          ))
        : null}
    </svg>
  );
}
